/**
 * Sessions Domain
 * Joining a scheduled session: the session a session link names and its
 * window, as the server keeps it, and team access codes. Sessions themselves
 * are scheduled in the portal, which the server's scheduler reads; none of
 * that lives here, and nor do the rules of a session's window: the server
 * sends when it opens and when it ends with the session.
 */

import { callRpcHttp } from '../services/nakama/http-rpc';
import { currentSeconds } from '../utils/use-now-seconds';

/** The game this client is: every way in signs in to it, and asks for its sessions. */
export const GAME_NAME = 'ponder-flight-game';

// ==================== SESSION LINKS ====================

/**
 * Session data returned by the get_world_session RPC: the portal's session
 * (BackendWorldSession on the server) and its window as the server keeps it
 * (WorldSessionWindow).
 */
export interface WorldSessionData {
  id: string;
  worldId: string;
  worldName: string;
  externalId: string;
  startTimeUtc: number;
  durationMinutes: number;
  cohortIds: string[];
  matchCreated: boolean;
  sessionScriptId?: string | null;
  globalAudioMuted?: boolean;
  languageCode?: string;
  teamAccessCodes?: { code: number; cohortId: string }[];
  /** The portal's: SCHEDULED, RUNNING, FINISHED once its match reached its end lobby, CANCELLED or DRAFT. */
  status?: string;
  /** When the session opens to its players, in Unix seconds: the scheduler makes its match then (SessionOpensUTC). */
  opensAtUtc: number;
  /** When it ends, in Unix seconds: its length after its start, or a script's fallback (SessionEndUTC). */
  endsAtUtc: number;
  /** Over already when the server answered: its end passed, or the portal marked it finished (SessionHasEnded). */
  ended: boolean;
}

type SessionWindowResult = 'valid' | 'early' | 'ended';

/**
 * Where the current moment falls in the session's window, as the server
 * keeps it: before it opens, open, or over — over when the server said so,
 * or once its end has passed while the page stayed open. The server joins by
 * the same rules, so a link never offers a join it would refuse as early or
 * over.
 *
 * @returns  'valid'   — player can join now
 * @returns  'early'   — session hasn't opened yet
 * @returns  'ended'   — session has already finished
 */
export function checkSessionWindow(
  session: Pick<WorldSessionData, 'opensAtUtc' | 'endsAtUtc' | 'ended'>,
  nowUtc?: number,
): SessionWindowResult {
  const now = nowUtc ?? currentSeconds();
  if (session.ended || now >= session.endsAtUtc) return 'ended';
  if (now < session.opensAtUtc) return 'early';
  return 'valid';
}

/**
 * Fetches session data from the Nakama server by session ID.
 * Used when a player arrives via a session invite link to validate the join window.
 * Throws on error; returns WorldSessionData on success.
 */
export async function fetchWorldSession(sessionId: string): Promise<WorldSessionData> {
  const data = await callRpcHttp('get_world_session', {
    game_name:  GAME_NAME,
    session_id: sessionId,
  });
  return data as WorldSessionData;
}

// ==================== TEAM CODE LOGIN ====================

/**
 * Data returned by the resolve_team_code RPC.
 * Contains everything the auth service needs to authenticate and join the match.
 */
export interface ResolvedTeamCodeData {
  code: number;
  cohortId: string;
  worldName: string;
  gameName: string;
  sessionExternalId: string;
  startTimeUtc: number;
  durationMinutes: number;
  sessionScriptId?: string | null;
}

/**
 * Resolves a numeric team code to session/cohort data via the Nakama RPC.
 * Throws if the code is not found or the request fails.
 */
export async function resolveTeamCode(code: string): Promise<ResolvedTeamCodeData> {
  const data = await callRpcHttp('resolve_team_code', { code });
  return data as ResolvedTeamCodeData;
}
